import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import axiosInstance from '../../../utils/axios';
import Config from '../../../utils/Config';
import { URLS } from '../../../utils/constants/urls';
import strings from '../../../common/Translation/Translate';
import { reportChartPaperSx } from './reportChartLayout';
import PageLoader from '../../../components/PageLoader';
import ReportKpiStrip from './ReportKpiStrip';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';
import { identifiers } from '../../../utils/constants/identifiers';
import { fetchFromStorage } from '../../../utils/storage';
import { downloadExportFile } from '../../../utils/validators/HelperFunctions';

type DemandLevel = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'OVERBOOKED';

interface VenueAvailability {
  venueName: string;
  status: string;
}

interface DemandDay {
  date: string;
  dateLabel: string;
  dayOfWeek: string;
  year: number;
  month: number;
  dayOfMonth: number;
  demandLevel: DemandLevel;
  orgHighDemand: boolean;
  highDemandCategory?: string;
  totalDemand: number;
  confirmed: number;
  tentative: number;
  rfq: number;
  waitlisted: number;
  cancelled: number;
  revenuePotential: number;
  confirmedRevenue: number;
  lostPendingPotential: number;
  availableVenues: number;
  venueAvailability: VenueAvailability[];
}

interface DemandCalendarResponse {
  kpis: {
    highDemandDates: number;
    overbookedDates: number;
    lostDemandDisplay: string;
    avgDemandPerWeekend: number;
    topDemandDay: string;
  };
  days: DemandDay[];
  tableRows: Array<{
    date: string;
    dateLabel: string;
    dayOfWeek: string;
    monthDayLabel: string;
    demand: number;
    confirmed: number;
    confirmedRevenueDisplay: string;
    highDemandCategory?: string;
  }>;
}

interface DayEventItem {
  event: string;
  venue: string;
  revenue: number;
  revenueDisplay: string;
  status: string;
}

interface DayEventsResponse {
  date: string;
  dateLabel: string;
  dayOfWeek: string;
  events: DayEventItem[];
}

const DEFAULT_CELL_STYLE = { bg: '#F9FAFB', color: '#374151', border: '#E5E7EB' };
const ORG_HIGH_DEMAND_STYLE = { bg: '#FFF3E0', color: '#E65100', border: '#F57F17' };

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TABLE_FIXED_HEIGHT = 480;
const CALENDAR_MAX_WIDTH = 360;

function formatAmount(value: number): string {
  return formatCurrencyWithSymbol(value);
}

function isDayClickable(day: DemandDay): boolean {
  return day.totalDemand > 0 || day.orgHighDemand || day.cancelled > 0;
}

function dayEventCount(day: DemandDay): number {
  return day.totalDemand + day.cancelled;
}

function statusChipColor(status: string): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'Confirmed':
      return 'success';
    case 'Tentative':
    case 'Enquiry':
      return 'warning';
    case 'Waitlisted':
      return 'info';
    case 'Cancelled':
      return 'error';
    case 'RFQ':
      return 'default';
    default:
      return 'default';
  }
}

function getCellColors(day: DemandDay) {
  if (day.orgHighDemand) {
    return ORG_HIGH_DEMAND_STYLE;
  }
  return DEFAULT_CELL_STYLE;
}

function buildDayTooltip(day: DemandDay): string {
  const parts: string[] = [day.dateLabel];
  if (day.orgHighDemand) {
    parts.push(strings.reports_demand_calendar_org_high_demand);
    if (day.highDemandCategory) {
      parts.push(`${strings.reports_demand_calendar_high_demand_category}: ${day.highDemandCategory}`);
    }
  }
  if (day.totalDemand > 0) {
    parts.push(`${day.totalDemand} ${strings.reports_demand_calendar_demand.toLowerCase()}`);
  }
  return parts.join(' · ');
}

function buildMonthCells(year: number, month: number, dayMap: Map<string, DemandDay>): (DemandDay | null)[] {
  const first = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
  const daysInMonth = first.daysInMonth();
  const mondayStart = (first.day() + 6) % 7;
  const cells: (DemandDay | null)[] = [];

  for (let i = 0; i < mondayStart; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const key = first.date(d).format('YYYY-MM-DD');
    cells.push(
      dayMap.get(key) ?? {
        date: key,
        dateLabel: first.date(d).format('D MMM'),
        dayOfWeek: first.date(d).format('ddd'),
        year,
        month,
        dayOfMonth: d,
        demandLevel: 'NONE',
        orgHighDemand: false,
        totalDemand: 0,
        confirmed: 0,
        tentative: 0,
        rfq: 0,
        waitlisted: 0,
        cancelled: 0,
        revenuePotential: 0,
        confirmedRevenue: 0,
        lostPendingPotential: 0,
        availableVenues: 0,
        venueAvailability: [],
      },
    );
  }
  return cells;
}

interface Props {
  onExportMessage?: (severity: 'success' | 'error', message: string) => void;
}

const DemandCalendar: FC<Props> = ({ onExportMessage }) => {
  const [visibleMonth, setVisibleMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DemandCalendarResponse | null>(null);
  const [selectedDay, setSelectedDay] = useState<DemandDay | null>(null);
  const [dayEvents, setDayEvents] = useState<DayEventsResponse | null>(null);
  const [dayEventsLoading, setDayEventsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const monthRangeKey = visibleMonth.format('YYYY-MM');
  const monthLabel = visibleMonth.format('MMMM YYYY');

  const fetchData = useCallback(async () => {
    const start = dayjs(`${monthRangeKey}-01`).startOf('month');
    const end = dayjs(`${monthRangeKey}-01`).endOf('month');
    try {
      setLoading(true);
      const { data: response } = await axiosInstance.post(URLS.demandCalendar, {
        startDate: start.format('DD-MM-YYYY'),
        endDate: end.format('DD-MM-YYYY'),
      });
      setData(response);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [monthRangeKey]);

  useEffect(() => {
    fetchData();
    setSelectedDay(null);
    setDayEvents(null);
  }, [fetchData]);

  useEffect(() => {
    if (!selectedDay) {
      setDayEvents(null);
      return;
    }

    let cancelled = false;
    const loadDayEvents = async () => {
      try {
        setDayEventsLoading(true);
        const { data: response } = await axiosInstance.post(URLS.demandCalendarDayEvents, {
          date: dayjs(selectedDay.date).format('DD-MM-YYYY'),
        });
        if (!cancelled) {
          setDayEvents(response);
        }
      } catch {
        if (!cancelled) {
          setDayEvents(null);
        }
      } finally {
        if (!cancelled) {
          setDayEventsLoading(false);
        }
      }
    };

    loadDayEvents();
    return () => {
      cancelled = true;
    };
  }, [selectedDay]);

  const dayMap = useMemo(() => {
    const map = new Map<string, DemandDay>();
    (data?.days ?? []).forEach(d => map.set(d.date, d));
    return map;
  }, [data]);

  const calendarCells = useMemo(
    () => buildMonthCells(visibleMonth.year(), visibleMonth.month() + 1, dayMap),
    [visibleMonth, dayMap],
  );

  const handleExport = () => {
    const start = visibleMonth.startOf('month');
    const end = visibleMonth.endOf('month');
    const token = fetchFromStorage(identifiers.access_token);
    setExporting(true);
    axios({
      url: `${Config.BaseURL + URLS.demandCalendarExport}`,
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      data: {
        startDate: start.format('DD-MM-YYYY'),
        endDate: end.format('DD-MM-YYYY'),
      },
    })
      .then(response => {
        let filename = 'Demand_Calendar.xlsx';
        const match = RegExp(/filename="?(.+?)"?$/).exec(response.headers['content-disposition'] ?? '');
        if (match) filename = match[1];
        downloadExportFile(response, 'xlsx', filename);
        onExportMessage?.('success', 'Demand Calendar exported successfully');
      })
      .catch(() => onExportMessage?.('error', 'Failed to export Demand Calendar'))
      .finally(() => setExporting(false));
  };

  const renderMonthNavigation = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        mb: 1.5,
        width: '100%',
        maxWidth: CALENDAR_MAX_WIDTH,
      }}>
      <IconButton
        size="small"
        aria-label={strings.reports_demand_calendar_previous_month}
        onClick={() => setVisibleMonth(m => m.subtract(1, 'month').startOf('month'))}>
        <ChevronLeftIcon />
      </IconButton>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          views={['month', 'year']}
          openTo="month"
          inputFormat={identifiers.month_input}
          value={visibleMonth}
          onChange={(newValue: Dayjs | null) => {
            if (newValue) {
              setVisibleMonth(newValue.startOf('month'));
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              inputProps={{
                ...params.inputProps,
                readOnly: true,
                'aria-label': strings.reports_demand_calendar_select_month,
              }}
              sx={{
                width: 168,
                '& .MuiInputBase-root': { fontWeight: 700, fontSize: '0.9rem' },
                '& .MuiInputBase-input': { textAlign: 'center', cursor: 'pointer', py: 0.75 },
              }}
            />
          )}
        />
      </LocalizationProvider>
      <IconButton
        size="small"
        aria-label={strings.reports_demand_calendar_next_month}
        onClick={() => setVisibleMonth(m => m.add(1, 'month').startOf('month'))}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );

  const renderCalendarGrid = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}>
      {renderMonthNavigation()}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
          width: '100%',
          maxWidth: CALENDAR_MAX_WIDTH,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, border: '2px solid #F57F17', bgcolor: '#FFF3E0' }} />
          <Typography variant="caption" color="text.secondary">
            {strings.reports_demand_calendar_org_high_demand}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Event count shown on dates with bookings
        </Typography>
      </Box>

      <Box sx={{ width: '100%', maxWidth: CALENDAR_MAX_WIDTH }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
            mb: 0.5,
          }}>
          {WEEKDAYS.map(wd => (
            <Typography
              key={wd}
              variant="caption"
              sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem' }}>
              {wd}
            </Typography>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {calendarCells.map((day, idx) => {
            if (!day) {
              return <Box key={`empty-${idx}`} sx={{ aspectRatio: '1', minHeight: 40 }} />;
            }
            const colors = getCellColors(day);
            const clickable = isDayClickable(day);
            return (
              <Tooltip key={day.date} title={buildDayTooltip(day)} arrow>
                <Box
                  onClick={() => (clickable ? setSelectedDay(day) : undefined)}
                  sx={{
                    aspectRatio: '1',
                    minHeight: 40,
                    borderRadius: 1,
                    border: day.orgHighDemand ? '2px solid #F57F17' : '1px solid',
                    borderColor: day.orgHighDemand ? '#F57F17' : colors.border,
                    bgcolor: colors.bg,
                    color: colors.color,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.1,
                    cursor: clickable ? 'pointer' : 'default',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': clickable
                      ? { transform: 'scale(1.06)', boxShadow: '0 4px 10px rgba(0,0,0,0.12)', zIndex: 1 }
                      : undefined,
                  }}>
                  <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.72rem', lineHeight: 1.1 }}>
                    {day.dayOfMonth}
                  </Typography>
                  {dayEventCount(day) > 0 && (
                    <Typography component="span" sx={{ fontWeight: 800, fontSize: '0.68rem', lineHeight: 1.1 }}>
                      {dayEventCount(day)}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  const renderDemandTable = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: TABLE_FIXED_HEIGHT + 36 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, flexShrink: 0 }}>
        {strings.reports_demand_calendar_high_demand_with_bookings} — {monthLabel}
      </Typography>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          height: TABLE_FIXED_HEIGHT,
          flexShrink: 0,
          overflow: 'auto',
        }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{strings.budget_text_month}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{strings.reports_demand_calendar_high_demand_category}</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                {strings.reports_demand_calendar_demand}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                {strings.reports_text_confirmed_bookings}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                {strings.reports_kpi_revenue}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data!.tableRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {strings.reports_demand_calendar_no_high_demand_bookings}
                </TableCell>
              </TableRow>
            ) : (
              data!.tableRows.map(row => (
                <TableRow
                  key={row.date}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    const day = dayMap.get(row.date);
                    if (day) setSelectedDay(day);
                  }}>
                  <TableCell>{row.monthDayLabel || `${row.dateLabel}, ${row.dayOfWeek}`}</TableCell>
                  <TableCell>{row.highDemandCategory || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {row.demand}
                  </TableCell>
                  <TableCell align="right">{row.confirmed}</TableCell>
                  <TableCell align="right">{row.confirmedRevenueDisplay || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Paper sx={reportChartPaperSx}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {strings.reports_demand_calendar_title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {strings.reports_demand_calendar_subtitle}
          </Typography>
        </Box>
        <Tooltip title={strings.reports_demand_calendar_export} arrow>
          <span>
            <IconButton
              onClick={handleExport}
              disabled={exporting || loading}
              sx={{
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: '8px',
                color: 'primary.main',
                p: 0.9,
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
              }}>
              <FileDownloadOutlined />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading && <PageLoader />}

      {!loading && data && (
        <>
          <ReportKpiStrip
            columns={5}
            items={[
              { label: strings.reports_demand_calendar_high_demand_dates, value: String(data.kpis.highDemandDates) },
              { label: strings.reports_demand_calendar_overbooked_dates, value: String(data.kpis.overbookedDates) },
              { label: strings.reports_demand_calendar_lost_demand, value: data.kpis.lostDemandDisplay || '—' },
              {
                label: strings.reports_demand_calendar_avg_weekend_demand,
                value: String(Math.round((data.kpis.avgDemandPerWeekend ?? 0) * 10) / 10),
              },
              { label: strings.reports_demand_calendar_top_demand_day, value: data.kpis.topDemandDay || '—' },
            ]}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2.5,
              mt: 2,
              alignItems: 'stretch',
            }}>
            <Box
              sx={{
                flex: { xs: '1 1 100%', lg: '0 0 40%' },
                maxWidth: { lg: '40%' },
                minWidth: 0,
              }}>
              {renderCalendarGrid()}
            </Box>
            <Box
              sx={{
                flex: { xs: '1 1 100%', lg: '0 0 60%' },
                maxWidth: { lg: '60%' },
                minWidth: 0,
              }}>
              {renderDemandTable()}
            </Box>
          </Box>
        </>
      )}

      <Dialog open={!!selectedDay} onClose={() => setSelectedDay(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              {selectedDay?.dateLabel} {selectedDay?.year}
            </Typography>
            {selectedDay && (
              <Typography variant="body2" color="text.secondary" display="block">
                {selectedDay.dayOfWeek}
                {selectedDay.orgHighDemand
                  ? ` · ${strings.reports_demand_calendar_org_high_demand}${
                      selectedDay.highDemandCategory ? `: ${selectedDay.highDemandCategory}` : ''
                    }`
                  : ''}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setSelectedDay(null)} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {dayEventsLoading && <PageLoader />}
          {!dayEventsLoading && (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{strings.reports_demand_calendar_event}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{strings.venueTitle}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      {strings.reports_kpi_revenue}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{strings.reports_demand_calendar_status}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!dayEvents || dayEvents.events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        {strings.reports_demand_calendar_no_events}
                      </TableCell>
                    </TableRow>
                  ) : (
                    dayEvents.events.map((event, index) => (
                      <TableRow key={`${event.event}-${event.venue}-${event.status}-${index}`} hover>
                        <TableCell>{event.event}</TableCell>
                        <TableCell>{event.venue}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {event.revenueDisplay || formatAmount(event.revenue)}
                        </TableCell>
                        <TableCell>
                          <Chip label={event.status} size="small" color={statusChipColor(event.status)} variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default DemandCalendar;
